import { useState } from 'react';
import { ApiError, useApi } from '../api/client.js';
import { Button, Card, Pill } from '../components/ui.js';
import { useAsync } from '../hooks/useAsync.js';

function formatRelative(tsMs: number): string {
  const delta = Date.now() - tsMs;
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day ago`;
}

/**
 * Shows the most recent successful rewrites + any failures needing attention.
 * Failures appear at top with a red pill and a "Rewrite now" retry button.
 * Successful rows are informational — the "heartbeat" that the pipeline is
 * live. Rows with `status='skipped'` are never written any more; we stopped
 * writing them because they were one per template per cron fire and drowned
 * out actionable signal.
 */
export function RewriteActivity() {
  const api = useApi();
  const recent = useAsync(() => api.recentRewrites(), [api]);
  const failures = useAsync(() => api.rewriteFailures(), [api]);
  const [rewritingId, setRewritingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRewriteNow = async (templateId: number) => {
    setError(null);
    setRewritingId(templateId);
    try {
      await api.rewriteNow(templateId);
      recent.refresh();
      failures.refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRewritingId(null);
    }
  };

  if (recent.state.status === 'loading' || failures.state.status === 'loading') {
    return (
      <Card title="Rewrite activity">
        <div className="h-24 animate-pulse" style={{ background: 'var(--bg-secondary)' }} />
      </Card>
    );
  }

  if (recent.state.status === 'error') {
    return (
      <Card title="Rewrite activity">
        <div role="alert" className="text-[13px]" style={{ color: 'var(--danger-text)' }}>
          Couldn't load rewrite activity: {recent.state.error.message}
        </div>
      </Card>
    );
  }

  const failedRows = failures.state.status === 'success' ? failures.state.data : [];
  const recentRows = recent.state.data;
  const failedCount = failedRows.length;

  return (
    <Card
      title={
        <span>
          Rewrite activity
          {failedCount > 0 && (
            <span className="ml-2 align-middle">
              <Pill tone="danger">{failedCount} failing</Pill>
            </span>
          )}
        </span>
      }
    >
      {error && (
        <div role="alert" className="text-[13px] mb-3" style={{ color: 'var(--danger-text)' }}>
          {error}
        </div>
      )}

      {failedCount > 0 && (
        <div className="mb-4">
          <div className="text-[12px] uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>
            Failures
          </div>
          <ul className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {failedRows.map((r) => (
              <li
                key={`f-${r.ts}-${r.opco}-${r.templateId}`}
                className="flex items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0">
                  <div className="text-[15px] font-medium truncate">
                    {r.opco} · template #{r.templateId}
                  </div>
                  <div className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                    {formatRelative(r.ts)} · {r.error ?? 'unknown error'}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Pill tone="danger">failed</Pill>
                  <Button
                    onClick={() => handleRewriteNow(r.templateId)}
                    disabled={rewritingId === r.templateId}
                    ariaLabel={`Rewrite template ${r.templateId} now`}
                  >
                    {rewritingId === r.templateId ? 'Rewriting…' : 'Rewrite now'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[12px] uppercase tracking-wide mb-2" style={{ color: 'var(--text-secondary)' }}>
        Recent rewrites
      </div>
      {recentRows.length === 0 ? (
        <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
          No rewrites yet. The rewriter picks up a template within ~60 s of you adding an
          {' '}<code>sfc-link://</code> href to it.
        </p>
      ) : (
        <ul className="divide-y" style={{ borderColor: 'var(--border)' }}>
          {recentRows.map((r) => (
            <li
              key={`r-${r.ts}-${r.opco}-${r.templateId}`}
              className="flex items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <div className="text-[15px] font-medium truncate">
                  {r.opco} · template #{r.templateId}
                </div>
                <div className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                  {formatRelative(r.ts)} · {r.linksRewritten} link{r.linksRewritten === 1 ? '' : 's'} rewritten
                </div>
              </div>
              <Pill tone="success">ok</Pill>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// Keep the old export name for any call-sites we haven't updated yet.
export { RewriteActivity as PendingRewrites };
